import { Link, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { Field, Form, Formik } from "formik";
import closeIcon from "../assets/images/close_icon.png";
import { Calendar, momentLocalizer } from "react-big-calendar";
import { useEffect, useRef, useState } from "react";
import { useTracking } from "../hooks/useTracking";
import { ActionCategory } from "../services/logging/logService";
import axios from "axios";
import Select from "../components/Select";
import { groupBy } from "lodash";
import moment from "moment";
import BeatLoader from "react-spinners/BeatLoader";
import { useSelector } from "react-redux";
import { handleLogout } from "../config/axios";
import { fetchUserInfo, getAllAgentCodes, processAgentCodeValue } from "../services/userService";
import decryptData from "../decrypt";

moment.locale("it", {
  months:
    "gennaio_febbraio_marzo_aprile_maggio_giugno_luglio_agosto_settembre_ottobre_novembre_dicembre".split(
      "_"
    ),
  monthsShort: "gen_feb_mar_apr_mag_giu_lug_ago_set_ott_nov_dic".split("_"),
  weekdays: "domenica_lunedì_martedì_mercoledì_giovedì_venerdì_sabato".split(
    "_"
  ),
  weekdaysShort: "dom_lun_mar_mer_gio_ven_sab".split("_"),
  weekdaysMin: "do_lu_ma_me_gi_ve_sa".split("_"),
  weekdaysParseExact: true,
  today: "dsada",
});

const localizer = momentLocalizer(moment);

// Define types for better type safety
/**
 * Funzione per registrare direttamente le azioni di ricerca senza usare il sistema di debounce
 */
const logSearchDirectly = (action: string, description: string, metadata: any) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No auth token found, cannot log search');
      return;
    }
    
    // Crea un payload di log simile a quello usato dal logService, ma con ID univoco
    const logMessage = {
      action: `search:${action}`,
      description,
      timestamp: new Date().toISOString(),
      pagePath: window.location.pathname,
      sessionId: `direct_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      metadata: {
        ...metadata,
        directLog: true,
        timestamp: new Date().toISOString()
      }
    };
    
    // Payload completo per l'API
    const payload = {
      level: 'info',
      serviceName: 'api',
      message: logMessage,
    };
    
    // Chiamata diretta all'API senza debounce
    axios.post('/nodeapi/writeLog', payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    .then(() => {
      console.log(`Log di ricerca inviato direttamente: ${action}`);
    })
    .catch(error => {
      console.error('Errore nell\'invio diretto del log:', error);
    });
  } catch (error) {
    console.error('Errore nella preparazione del log diretto:', error);
  }
};

type Agent = {
  value: string;
  label: string;
  db2_user_id: string;
  raw_codes?: string[];
};

type Appointment = {
  id: string;
  title: string;
  message: string;
  date: string;
  time: string;
  customer?: {
    codice_cliente: string;
    ragione_sociale: string;
  };
};

type CalendarEvent = {
  appointmentId: string;
  title: string;
  start: Date;
  end: Date;
};

export default function Appuntamenti() {
  const formikRef = useRef<any>(null);
  const navigate = useNavigate();
  const tracking = useTracking();

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [filters, setFilters] = useState<{ searchFilters?: any[] }>({});
  const [loading, setLoading] = useState(false);
  const [isSearched, setIsSearched] = useState(false);

  const [userRole, setUserRole] = useState("");
  const [agentFilters, setAgentFilters] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(() => {
    const savedFilters = sessionStorage.getItem("appointments-filters");
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      return parsedFilters.selectedAgent || null;
    }
    return null;
  });

  const [theme, setTheme] = useState("light");
  const currentTheme = useSelector((state: any) => state.menu.theme);

  useEffect(() => {
    setTheme(currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    (async () => {
      const handleResize = async () => {
        if (window.screen.availWidth <= 768) {
          formikRef.current?.setFieldValue("vista", "lista");
        }
      };

      ["resize", "load"].forEach((e) => {
        window.addEventListener(e, handleResize);
      });

      return () => {
        ["resize", "load"].forEach((e) => {
          window.removeEventListener(e, handleResize);
        });
      };
    })();
  }, []);

  const fetchFilters = async () => {
    try {
      setLoading(true);
      console.log("Agenti per filtri:", agentFilters);
      
      // Get all agent codes using the centralized function
      const processedAgentCodes = await getAllAgentCodes();
      
      console.log("Codici agente elaborati per /utenti:", processedAgentCodes);
      
      const requestBody = {
        agenti: processedAgentCodes,
        page: 1,
        pageSize: 1000,
      };

      const request = await fetch(`/nodeapi/utenti`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(requestBody),
      });



      if (request.status !== 200 && request.status !== 201) {
          handleLogout(request, "/nodeapi/utenti", JSON.stringify(requestBody));
         return;
       }

      const result = await request.json();
      
      // Decodifica i dati criptati
      if (result.encryptedData && result.encryptedKey && result.iv) {
        console.log("Decodifica dati criptati...");
        const decrypted = decryptData(
          result.encryptedData,
          result.encryptedKey,
          result.iv
        );
        
        if (decrypted) {
          const decryptedJson = JSON.parse(decrypted);
          
          // Mappa i dati: estrai CODICE_CLIENTE e RAGIONE_SOCIALE
          const mappedFilters = Array.isArray(decryptedJson.data)
            ? decryptedJson.data.map((item: any) => ({
                codice_cliente: item.CODICE_CLIENTE,
                ragione_sociale: item.RAGIONE_SOCIALE,
              }))
            : [];
            
          // Imposta filters come oggetto con la proprietà searchFilters
          setFilters({ searchFilters: mappedFilters });
        }
      } else {
        // Fallback per dati non criptati
        // Mappa i dati: estrai CODICE_CLIENTE e RAGIONE_SOCIALE
        const mappedFilters = Array.isArray(result.data)
          ? result.data.map((item: any) => ({
              codice_cliente: item.CODICE_CLIENTE,
              ragione_sociale: item.RAGIONE_SOCIALE,
            }))
          : [];
        // Imposta filters come oggetto con la proprietà searchFilters
        setFilters({ searchFilters: mappedFilters });
      }
    } catch (e) {
      console.error("Errore durante il recupero dei filtri:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("Agent filters changed:", agentFilters);
    if (agentFilters.length > 0) {
      fetchFilters();
    }
  }, [agentFilters]);

  useEffect(() => {
    const initializeData = async () => {
      // Fetch agents first to establish user role and agent list
      await fetchUserData();

      // Then fetch appointments
      const savedFilters = sessionStorage.getItem("appointments-filters");
      let initialFilters = {};
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters);
        initialFilters = {
          ...parsedFilters,
          codice_agente: parsedFilters.selectedAgent?.value,
        };
      }
      await fetchAppointments(initialFilters);
    };
    initializeData();
  }, []);

  // Renamed from fetchListData to fetchUserData for clarity
  const fetchUserData = async (): Promise<string[] | undefined> => {
    try {
      // Use the centralized userService to fetch user info
      const { user, isSupervisor, agentCodes, agents } = await fetchUserInfo();
      
      if (!user) {
        console.error("No user data returned from fetchUserInfo");
        return [];
      }
      
      setUserRole(isSupervisor ? "supervisor" : "agent");
      
      // Raccogli tutti i codici agente pertinenti all'utente
      let allRelevantCodes: string[] = [];
      
      // Aggiungi i codici dell'utente corrente
      allRelevantCodes = [...allRelevantCodes, ...agentCodes];
      
      if (isSupervisor && agents.length > 0) {
        // Process supervisor's team agents
        const formattedAgents = agents.map((member) => {
          // IMPORTANTE: Non unire i codici con virgole per i valori
          // ma teniamo l'array originale e lo convertiamo in JSON
          const codeValue = JSON.stringify(member.all_codes);
          
          // Aggiungi tutti i codici di questo agente alla lista completa
          if (member.all_codes && member.all_codes.length > 0) {
            allRelevantCodes = [...allRelevantCodes, ...member.all_codes];
          }
          
          return {
            value: codeValue, // Store JSON stringified array: ["010","031"]
            label: `${member.first_name} ${member.last_name}`,
            db2_user_id: codeValue, // Mantiene lo stesso formato
            raw_codes: member.all_codes // Conserva l'array originale
          };
        });

        setAgentFilters(formattedAgents);
        
        // Log di tutti i codici raccolti
        console.log("Tutti i codici agente pertinenti (supervisor+agents):", allRelevantCodes);
        
        // Rimuovi duplicati e valori vuoti
        const uniqueCodes = [...new Set(allRelevantCodes)].filter(Boolean);
        console.log("Codici unici per le API:", uniqueCodes);
        
        return uniqueCodes;
      } else {
        // Per agenti normali: usa i propri codici
        // IMPORTANTE: Mantieni l'array originale
        const codeValue = JSON.stringify(agentCodes);
        setAgentFilters([{
          value: codeValue,
          label: `${user.firstName || ''} ${user.lastName || ''}`,
          db2_user_id: codeValue,
          raw_codes: agentCodes
        }]);
        
        // Log di tutti i codici raccolti (solo quelli dell'agente)
        console.log("Codici agente singolo:", agentCodes);
        
        return agentCodes;
      }
    } catch (error) {
      console.error("Error fetching user data from keycloak:", error);
      return [];
    }
  };

  const fetchAppointments = async (filters: any = {}) => {
    try {
      setLoading(true);
      const data: any = {};
      if (filters) {
        const { search, date, to, agentCodesForApi } = filters;
        if (search) {
          const searchExpanded = JSON.parse(search.value);
          data.search = {
            codice_cliente: searchExpanded.codice_cliente,
            ragione_sociale: searchExpanded.ragione_sociale,
          };
        }
        if (date) data.date = date;
        if (to) data.to = to;
        
        // Gestisci array di codici agente
        if (agentCodesForApi && Array.isArray(agentCodesForApi) && agentCodesForApi.length > 0) {
          console.log("Utilizzando codici agente per richiesta API:", agentCodesForApi);
          if (agentCodesForApi.length === 1) {
            data.codice_agente = agentCodesForApi[0];
          } else {
            data.agenti = agentCodesForApi;
          }
        }
      }

      // Ottieni i dati dell'utente da Keycloak
      const keycloakResponse = await fetch("/nodeapi/keycloak-protected", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (keycloakResponse.status !== 200 && keycloakResponse.status !== 201) {
          handleLogout(keycloakResponse, "/nodeapi/keycloak-protected", JSON.stringify(""));
         return;
       }

      const keycloakResult = await keycloakResponse.json();
      console.log("Keycloak user data:", keycloakResult);

      if (keycloakResult.user) {
        // Aggiungiamo l'utente corrente e il suo ruolo alla richiesta
        data.user_id = keycloakResult.user.id;
        data.roles = keycloakResult.user.roles;

        // Se è un supervisor, aggiungiamo anche gli ID dei membri del gruppo
        if (
          keycloakResult.user.roles.includes("supervisor") &&
          keycloakResult.user.groupMembers
        ) {
          data.groupMemberIds = keycloakResult.user.groupMembers.map(
            (member) => member.id
          );
        }
      }

      console.log("qqq: ", data);

      const request = await fetch(`/nodeapi/appointments/list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(data),
      });

      if (request.status !== 200 && request.status !== 201) {
          handleLogout(request, "/nodeapi/appointments/list", JSON.stringify(data));
         return;
       }

      const response = await request.json();
      const parsed = JSON.parse(decryptData(response.encryptedData, response.encryptedKey, response.iv));

      // Set appointments data
      setAppointments(parsed.appointments || []);

      // REMOVED: No longer getting agent info from the appointments list call

      // Transform appointments into calendar events
      const events = (parsed.appointments || []).map(
        (appointment: Appointment) => {
          const { title, message, date, time } = appointment;
          const splittedTime = time.split(":");
          const hour = splittedTime[0];
          const minute = splittedTime[1];
          const slotDate = moment(date);
          slotDate.set({
            hour: parseInt(hour),
            minute: parseInt(minute),
          });
          return {
            appointmentId: appointment.id,
            title: `${title} - ${message}`,
            start: slotDate.toDate(),
            end: slotDate.add(2, "hours").toDate(),
          };
        }
      );

      setEvents(events);
    } catch (e) {
      console.error("Error fetching appointments:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = async (
    value: any,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue("search", value);
    
    // 1. Track search action with standard tracking system
    tracking.trackSearch('Ricerca Appuntamenti ' + new Date().getTime(), undefined, {
      filters: {
        search: value,
        date: formikRef.current?.values?.date,
        to: formikRef.current?.values?.to,
        selectedAgent
      },
      userRole,
      timestamp: new Date().toISOString()
    });
    
    // 2. Track search action with direct method
    logSearchDirectly('appuntamenti_search', 'Ricerca Appuntamenti (Direct Log)', {
      filters: {
        search: value,
        date: formikRef.current?.values?.date,
        to: formikRef.current?.values?.to,
        selectedAgent
      },
      userRole,
      timestamp: new Date().toISOString()
    });
    
    if (!loading) {
      await fetchAppointments({ ...formikRef.current.values, search: value });
      setIsSearched(true);
    }
  };

  const handleDateChange = async (
    value: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const val = value.target.value;
    setFieldValue("date", val);
    
    // 1. Track date change action with standard tracking system
    tracking.trackSearch('Appuntamenti Data Dal ' + new Date().getTime(), undefined, {
      filters: {
        search: formikRef.current?.values?.search,
        date: val,
        to: formikRef.current?.values?.to,
        selectedAgent
      },
      userRole,
      timestamp: new Date().toISOString()
    });
    
    // 2. Track date change with direct method
    logSearchDirectly('appuntamenti_change_date', 'Appuntamenti Cambio Data (Direct Log)', {
      filters: {
        search: formikRef.current?.values?.search,
        date: val,
        to: formikRef.current?.values?.to,
        selectedAgent
      },
      userRole,
      timestamp: new Date().toISOString()
    });
    
    if (!loading) {
      await fetchAppointments({ ...formikRef.current.values, date: val });
    }
  };

  const handleToDateChange = async (
    value: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const val = value.target.value;
    setFieldValue("to", val);
    
    // 1. Track to date change action with standard tracking system
    tracking.trackSearch('Appuntamenti Data Al ' + new Date().getTime(), undefined, {
      filters: {
        search: formikRef.current?.values?.search,
        date: formikRef.current?.values?.date,
        to: val,
        selectedAgent
      },
      userRole,
      timestamp: new Date().toISOString()
    });
    
    // 2. Track to date change with direct method
    logSearchDirectly('appuntamenti_change_to_date', 'Appuntamenti Cambio Data Al (Direct Log)', {
      filters: {
        search: formikRef.current?.values?.search,
        date: formikRef.current?.values?.date,
        to: val,
        selectedAgent
      },
      userRole,
      timestamp: new Date().toISOString()
    });
    
    if (!loading) {
      await fetchAppointments({ ...formikRef.current.values, to: val });
    }
  };

  const clearFiltri = async (
    e: React.MouseEvent<HTMLButtonElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    e.preventDefault();
    
    // 1. Log clear filters action with standard system
/*     tracking.logAction(
      ActionCategory.USER_ACTION,
      "clear_filters",
      "User cleared all filters in Appuntamenti",
      {
        userRole,
        timestamp: new Date().toISOString()
      }
    ); */

    // 2. Log clear filters with direct method
    logSearchDirectly('appuntamenti_clear_filters', 'Reset Filtri Appuntamenti (Direct Log)', {
      userRole,
      timestamp: new Date().toISOString()
    });
    
    setFieldValue("search", "");
    setFieldValue("date", "");
    setFieldValue("to", "");
    if (!loading) {
      await fetchAppointments();
    }
  };

  const handleEventClick = (event: CalendarEvent) => {
    const { appointmentId, start } = event;
    const startDate = moment(start);
    if (startDate.isAfter()) {
      navigate(`/appuntamenti/${appointmentId}/edit`);
    }
  };

  const [width, setWidth] = useState<number>(window.innerWidth);

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }

  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  const isMobile = width <= 768;

  return (
    <div className="px-5 py-8 sm:px-12 columns-3xl lg:columns-auto cu-of">
      <div className="sm:max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between relative mt-[100px] md:mt-50 mb-cu-m">
        <div className="w-full text-center mb-4 md:mb-0 ">
          <h2 className="text-4xl text-blue-500 font-bold">Appuntamenti</h2>
        </div>
      </div>

      <Formik
        innerRef={formikRef}
        enableReinitialize={true}
        initialValues={{
          search: sessionStorage.getItem("appointments-filters")
            ? JSON.parse(sessionStorage.getItem("appointments-filters")!).search
            : "",
          date: sessionStorage.getItem("appointments-filters")
            ? JSON.parse(sessionStorage.getItem("appointments-filters")!).date
            : "",
          to: sessionStorage.getItem("appointments-filters")
            ? JSON.parse(sessionStorage.getItem("appointments-filters")!).to
            : "",
          codice_agente: sessionStorage.getItem("appointments-filters")
            ? JSON.parse(sessionStorage.getItem("appointments-filters")!)
                .codice_agente
            : "",
          vista: "schede",
        }}
        validateOnChange={false}
        onSubmit={fetchAppointments}
      >
        {({ isSubmitting, values, resetForm, setFieldValue }) => (
          <Form>
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <BeatLoader color="#2563eb" />
              </div>
            ) : (
              <div className="relative">
                <div className="flex flex-col lg:flex-row gap-8">
                  {/* Colonna dei filtri */}
                  <div className="w-full lg:w-1/5 space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="text-blue-600 text-xl">Filtri</h4>
                      <button
                        onClick={(e) => clearFiltri(e, setFieldValue)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <span className="sr-only">Pulisci filtri</span>
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>

                    {userRole === "supervisor" && agentFilters.length > 0 && (
                      <div className="w-full md:w-auto mb-4">
                        <Select
                          options={agentFilters}
                          placeholder="Seleziona Agente"
                          value={selectedAgent}
                          onChange={(val: Agent | null) => {
                            setSelectedAgent(val);
                            
                            // Prepara codici agente per richiesta API usando la funzione centralizzata
                            let agentCodesForApi = [];
                            if (val) {
                              agentCodesForApi = processAgentCodeValue(val.value);
                            }
                            
                            console.log("Codici agente selezionati per API:", agentCodesForApi);
                            
                            const filtersToSave = {
                              ...formikRef.current.values,
                              selectedAgent: val,
                              agentCodesForApi: agentCodesForApi,
                            };
                            sessionStorage.setItem(
                              "appointments-filters",
                              JSON.stringify(filtersToSave)
                            );
                            fetchAppointments({
                              ...formikRef.current.values,
                              agentCodesForApi: agentCodesForApi,
                            });
                          }}
                          classNames={{
                            container: (state: any) => `w-full md:w-auto`,
                            control: (state: any) =>
                              `w-full min-h-c border border-neutral-300 dark:border-neutral-400`,
                            singleValue: (state: any) =>
                              `!text-neutral-500 dark:!text-neutral-400`,
                            option: (state: any) =>
                              `hover:bg-blue-500 hover:text-white`,
                          }}
                        />
                      </div>
                    )}

                    <Select
                      classNames={{
                        container: () => `w-full`,
                        control: () =>
                          `w-full min-h-c border border-neutral-300`,
                      }}
                      onChange={(val) => handleSearchChange(val, setFieldValue)}
                      value={values.search}
                      isClearable={true}
                      placeholder="Cliente / Codice Cliente"
                      options={filters?.searchFilters?.map((sf, i) => ({
                        value: JSON.stringify(sf),
                        label: `${sf.ragione_sociale} (${sf.codice_cliente})`,
                      }))}
                    />

                    <div className="date-input-container">
                      <Field
                        type="date"
                        name="date"
                        onChange={(val: React.ChangeEvent<HTMLInputElement>) =>
                          handleDateChange(val, setFieldValue)
                        }
                        className={classNames({
                          "cu-color-picker w-full min-h-c placeholder-current::placeholder min-h-c px-3 block dark:text-neutral-400 font-semibold border bg-transparent focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none rounded":
                            true,
                        })}
                        placeholder="Da"
                        style={{
                          colorScheme: theme === "dark" ? "dark" : "light",
                        }}
                      />
                    </div>
                    <div className="date-input-container">
                      <Field
                        type="date"
                        name="to"
                        onChange={(val: React.ChangeEvent<HTMLInputElement>) =>
                          handleToDateChange(val, setFieldValue)
                        }
                        className={classNames({
                          "cu-color-picker w-full min-h-c placeholder-current::placeholder min-h-c px-3 block dark:text-neutral-400 font-semibold border bg-transparent focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none rounded":
                            true,
                        })}
                        placeholder="A"
                        style={{
                          colorScheme: theme === "dark" ? "dark" : "light",
                          width: "100%"
                        }}
                      />
                    </div>
                  </div>

                  {/* Colonna principale (calendario/lista) */}
                  <div className="w-full lg:w-3/5">
                    {!loading && (values.vista === "lista" || isMobile) && (
                      <div className="space-y-8">
                        {appointments &&
                          Object.entries(
                            groupBy(appointments, (iteratee) => {
                              return moment(iteratee.date).format("MMMM YYYY");
                            })
                          ).map((ap, index) => {
                            const month = ap[0];
                            const realAppointments = ap[1];
                            return (
                              <div className="mb-16" key={index}>
                                <div className="mb-7">
                                  <h4 className="text-4xl font-bold text-gray-700">
                                    {month}
                                  </h4>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                  {realAppointments &&
                                    realAppointments.map(
                                      (appointment, index2) => {
                                        const { date, time, title, customer } =
                                          appointment;
                                        const splittedTime = time.split(":");
                                        const hour = splittedTime[0];
                                        const minute = splittedTime[1];
                                        const slotDate = moment(date);
                                        slotDate.set({
                                          hour: parseInt(hour),
                                          minute: parseInt(minute),
                                        });
                                        return (
                                          <div
                                            key={index2}
                                            className="bg-white shadow-md hover:shadow-lg transition-shadow duration-300 p-4 rounded-lg"
                                          >
                                            <div className="flex justify-between items-center">
                                              <h2 className="text-2xl font-semibold text-blue-500 mb-3">
                                                {slotDate.format(
                                                  "dddd DD/MM/YYYY"
                                                )}
                                              </h2>
                                              {/* Azioni rapide */}
                                              <div className="flex gap-2">
                                                <button
                                                  className="text-blue-600 hover:text-blue-700"
                                                  onClick={() =>
                                                    navigate(
                                                      `/appuntamenti/${appointment.id}/edit`
                                                    )
                                                  }
                                                  type="button"
                                                >
                                                  <svg
                                                    fill="#2563eb"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 494.936 494.936"
                                                    className="h-5 w-5 mb-3"
                                                  >
                                                    <g>
                                                      <g>
                                                        <path d="M389.844,182.85c-6.743,0-12.21,5.467-12.21,12.21v222.968c0,23.562-19.174,42.735-42.736,42.735H67.157 c-23.562,0-42.736-19.174-42.736-42.735V150.285c0-23.562,19.174-42.735,42.736-42.735h267.741c6.743,0,12.21-5.467,12.21-12.21 s-5.467-12.21-12.21-12.21H67.157C30.126,83.13,0,113.255,0,150.285v267.743c0,37.029,30.126,67.155,67.157,67.155h267.741 c37.03,0,67.156-30.126,67.156-67.155V195.061C402.054,188.318,396.587,182.85,389.844,182.85z" />
                                                        <path d="M483.876,20.791c-14.72-14.72-38.669-14.714-53.377,0L221.352,229.944c-0.28,0.28-3.434,3.559-4.251,5.396l-28.963,65.069 c-2.057,4.619-1.056,10.027,2.521,13.6c2.337,2.336,5.461,3.576,8.639,3.576c1.675,0,3.362-0.346,4.96-1.057l65.07-28.963 c1.83-0.815,5.114-3.97,5.396-4.25L483.876,74.169c7.131-7.131,11.06-16.61,11.06-26.692 C494.936,37.396,491.007,27.915,483.876,20.791z M466.61,56.897L257.457,266.05c-0.035,0.036-0.055,0.078-0.089,0.107 l-33.989,15.131L238.51,247.3c0.03-0.036,0.071-0.055,0.107-0.09L447.765,38.058c5.038-5.039,13.819-5.033,18.846,0.005 c2.518,2.51,3.905,5.855,3.905,9.414C470.516,51.036,469.127,54.38,466.61,56.897z" />
                                                      </g>
                                                    </g>
                                                  </svg>
                                                </button>
                                              </div>
                                            </div>
                                            <hr className="border-1 border-neutral-400" />
                                            <div className="flex justify-between items-start gap-10 my-5">
                                              <div>
                                                <p className="text-xl text-gray-400">
                                                  {slotDate.format("HH:mm")}
                                                </p>
                                                <p className="text-xl text-gray-400">
                                                  {slotDate
                                                    .add(3, "hour")
                                                    .format("HH:mm")}
                                                </p>
                                              </div>
                                              <div className="text-start">
                                                <h4 className="text-xl font-semibold text-gray-900">
                                                  {title}
                                                </h4>
                                                <p className="text-lg text-gray-700">
                                                  {customer?.codice_cliente} -{" "}
                                                  {customer?.ragione_sociale}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }
                                    )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                    {!loading &&
                      ((isMobile && isSearched) || !isMobile) &&
                      (values.vista === "schede" || !isMobile) && (
                        <div className="h-[600px] lg:h-[800px]">
                          <Calendar
                            localizer={localizer}
                            events={events}
                            messages={{
                              date: "Data",
                              time: "Tempo",
                              event: "Evento",
                              allDay: "Tutto il giorno",
                              week: "Settimana",
                              work_week: "Settimana di lavoro",
                              day: "Giorno",
                              month: "Mese",
                              previous: "Indietro",
                              next: "Prossimo",
                              yesterday: "Ieri",
                              tomorrow: "Domani",
                              today: "Oggi",
                              agenda: "Ordine del giorno",
                              noEventsInRange:
                                "Non ci sono eventi in questo intervallo",
                            }}
                            formats={{
                              timeGutterFormat: "HH:mm",
                            }}
                            dateFormat="HH:mm"
                            onSelectEvent={(event) => handleEventClick(event)}
                            selectable
                            defaultView="week"
                            locale="it-IT"
                            views={["week"]}
                            min={new Date(0, 0, 0, 8, 0)} // Ora di inizio
                            max={new Date(0, 0, 0, 20, 0)} // Ora di fine
                          />
                        </div>
                      )}
                  </div>

                  {/* Colonna della vista */}
                  <div className="w-full lg:w-1/5 hidden lg:block">
                    <div className="">
                      <div className="flex justify-between items-center mb-5">
                        <div className="flex gap-5">
                          <h4 className="text-blue-600 text-xl">Vista</h4>
                        </div>
                      </div>

                      <div>
                        <div className="">
                          <div className="flex">
                            <label
                              htmlFor="vista_schede"
                              className={classNames({
                                "flex justify-center min-h-c p-2 w-full border border-r-0 border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white":
                                  true,
                                "bg-blue-600 text-white":
                                  values.vista === "schede",
                              })}
                            >
                              <input
                                type="radio"
                                id="vista_schede"
                                name="vista"
                                className="hidden shrink-0 mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => setFieldValue("vista", e.target.value)}
                                value="schede"
                                checked={values.vista === "schede"}
                              />
                              <span className="">Settimana</span>
                            </label>

                            <label
                              htmlFor="vista_lista"
                              className={classNames({
                                "flex justify-center min-h-c p-2 w-full border border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white":
                                  true,
                                "bg-blue-600 text-white":
                                  values.vista === "lista",
                              })}
                            >
                              <input
                                type="radio"
                                id="vista_lista"
                                name="vista"
                                className="hidden shrink-0 mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => setFieldValue("vista", e.target.value)}
                                value="lista"
                                checked={values.vista === "lista"}
                              />
                              <span className="">Giorno</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Link to="/appuntamenti/new">
                  <button
                    type="button"
                    className="fixed bottom-8 right-8 bg-blue-600 text-white hover:bg-blue-700 p-4 rounded-full shadow-lg text-2xl flex items-center justify-center transition-transform transform hover:scale-105"
                    aria-label="Nuovo Appuntamento"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      className="w-6 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </button>
                </Link>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
}
